import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  ImagePlus,
  Link2,
  Plus,
  Save,
  Trash2,
  ChevronUp,
  ChevronDown,
  Video,
} from "lucide-react";
import {
  DEFAULT_BLOG_CATEGORIES,
  SECONDARY_KEYWORD_OPTIONS,
  TAG_OPTIONS,
} from "../../config/adminConfig";
import PillInput from "./PillInput";
import { blogService } from "../../services/blog.service";
import { uploadService } from "../../services/upload.service";
import {
  DEFAULT_SECTION_STYLES,
  FONT_FAMILIES,
  FONT_SIZES,
  FONT_STYLES,
  estimateReadTime,
  paragraphsFromText,
  parseMediaUrl,
  readTimeMinutes,
  slugify,
  textFromParagraphs,
} from "../../utils/blogHelpers";
import styles from "./Admin.module.css";

const emptySection = () => ({
  type: "section",
  heading: "",
  body: "",
  ...DEFAULT_SECTION_STYLES,
  image: "",
  imageAlt: "",
  video: "",
  videoUrl: "",
  videoCaption: "",
  sourceUrl: "",
  sourceLabel: "",
});

const emptyImage = () => ({
  type: "image",
  src: "",
  alt: "",
  caption: "",
});

const emptyVideo = () => ({
  type: "video",
  src: "",
  url: "",
  caption: "",
});

const emptyEmbed = () => ({
  type: "embed",
  url: "",
  label: "",
});

const StyleControls = ({ prefix, values, onChange }) => (
  <div className={styles.styleRow}>
    <div className={styles.field}>
      <label>Font style</label>
      <select
        value={values[`${prefix}FontFamily`]}
        onChange={(event) => onChange(`${prefix}FontFamily`, event.target.value)}
      >
        {FONT_FAMILIES.map((font) => (
          <option key={font.value} value={font.value}>
            {font.label}
          </option>
        ))}
      </select>
    </div>
    <div className={styles.field}>
      <label>Font size</label>
      <select
        value={values[`${prefix}FontSize`]}
        onChange={(event) => onChange(`${prefix}FontSize`, event.target.value)}
      >
        {FONT_SIZES.map((size) => (
          <option key={size} value={size}>
            {size}
          </option>
        ))}
      </select>
    </div>
    <div className={styles.field}>
      <label>Weight / style</label>
      <select
        value={values[`${prefix}FontStyle`]}
        onChange={(event) => onChange(`${prefix}FontStyle`, event.target.value)}
      >
        {FONT_STYLES.map((item) => (
          <option key={item.value} value={item.value}>
            {item.label}
          </option>
        ))}
      </select>
    </div>
  </div>
);

const BlogEditorPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditing = Boolean(id);

  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState("");
  const [error, setError] = useState("");
  const [form, setForm] = useState({
    id: "",
    title: "",
    slug: "",
    category: DEFAULT_BLOG_CATEGORIES[0],
    customCategory: "",
    primaryKeyword: "",
    secondaryKeywords: [],
    tags: [],
    readTime: "",
    shortDescription: "",
    image: "",
    bannerImage: "",
    featured: false,
    published: true,
    content: [emptySection()],
  });

  useEffect(() => {
    if (!isEditing) return;

    blogService.getBlogById(id).then((blog) => {
      if (!blog) {
        setError("This blog could not be found.");
        return;
      }

      const knownCategory = DEFAULT_BLOG_CATEGORIES.includes(blog.category);
      setForm({
        ...blog,
        category: knownCategory ? blog.category : "custom",
        customCategory: knownCategory ? "" : blog.category,
        secondaryKeywords: blog.secondaryKeywords || [],
        tags: blog.tags || [],
        readTime: readTimeMinutes(blog.readTime),
        content: blog.content.length
          ? blog.content.map((block) =>
              block.type === "section"
                ? { ...emptySection(), ...block, body: textFromParagraphs(block.paragraphs) }
                : block
            )
          : [emptySection()],
      });
    });
  }, [id, isEditing]);

  const categoryOptions = useMemo(() => DEFAULT_BLOG_CATEGORIES, []);
  const suggestedReadTime = useMemo(
    () => readTimeMinutes(estimateReadTime(form.content)),
    [form.content]
  );

  const updateField = (name, value) => {
    setForm((current) => ({
      ...current,
      [name]: value,
      slug: name === "title" && !isEditing ? slugify(value) : current.slug,
    }));
  };

  const updateBlock = (index, nextBlock) => {
    setForm((current) => ({
      ...current,
      content: current.content.map((block, blockIndex) =>
        blockIndex === index ? nextBlock : block
      ),
    }));
  };

  const updateBlockField = (index, name, value) => {
    setForm((current) => ({
      ...current,
      content: current.content.map((block, blockIndex) =>
        blockIndex === index ? { ...block, [name]: value } : block
      ),
    }));
  };

  const moveBlock = (index, direction) => {
    setForm((current) => {
      const next = [...current.content];
      const target = index + direction;
      if (target < 0 || target >= next.length) return current;
      [next[index], next[target]] = [next[target], next[index]];
      return { ...current, content: next };
    });
  };

  const removeBlock = (index) => {
    setForm((current) => ({
      ...current,
      content: current.content.filter((_, blockIndex) => blockIndex !== index),
    }));
  };

  const addBlock = (type) => {
    const next =
      type === "image"
        ? emptyImage()
        : type === "video"
          ? emptyVideo()
          : type === "embed"
            ? emptyEmbed()
            : emptySection();

    setForm((current) => ({
      ...current,
      content: [...current.content, next],
    }));
  };

  const handleImageUpload = async (file, onReady) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      setError("Please choose an image file.");
      return;
    }

    try {
      setError("");
      setUploading("Uploading image to Cloudinary...");
      onReady(await uploadService.uploadImage(file));
    } catch (uploadError) {
      setError(uploadError.message);
    } finally {
      setUploading("");
    }
  };

  const handleVideoUpload = async (file, onReady) => {
    if (!file) return;
    if (!file.type.startsWith("video/")) {
      setError("Please choose a video file.");
      return;
    }

    try {
      setError("");
      setUploading("Uploading video to Cloudinary...");
      onReady(await uploadService.uploadVideo(file));
    } catch (uploadError) {
      setError(uploadError.message);
    } finally {
      setUploading("");
    }
  };

  const handleSave = async (event) => {
    event.preventDefault();
    setError("");

    const category =
      form.category === "custom" ? form.customCategory.trim() : form.category;

    if (!form.title.trim() || !form.shortDescription.trim() || !category) {
      setError("Title, category, and short description are required.");
      return;
    }

    if (!form.primaryKeyword.trim() || form.secondaryKeywords.length === 0) {
      setError("Add a primary keyword and at least one secondary keyword.");
      return;
    }

    if (form.tags.length === 0) {
      setError("Add at least one tag.");
      return;
    }

    if (!form.bannerImage && !form.image) {
      setError("Please upload a banner or card image.");
      return;
    }

    let content;
    try {
      content = form.content
      .map((block) => {
        if (block.type === "image") {
          return block.src
            ? {
                type: "image",
                src: block.src,
                alt: (block.alt || "").trim(),
                caption: (block.caption || "").trim(),
              }
            : null;
        }

        if (block.type === "video") {
          if (!block.src && !block.url) return null;
          if (block.url && !parseMediaUrl(block.url)) {
            throw new Error("Please enter a valid video URL.");
          }
          return {
            type: "video",
            src: block.src || "",
            url: (block.url || "").trim(),
            caption: (block.caption || "").trim(),
          };
        }

        if (block.type === "embed") {
          if (!block.url.trim()) return null;
          if (!parseMediaUrl(block.url)) {
            throw new Error("Please enter a valid YouTube or source URL.");
          }
          return {
            type: "embed",
            url: block.url.trim(),
            label: (block.label || "").trim(),
          };
        }

        const paragraphs = paragraphsFromText(block.body || textFromParagraphs(block.paragraphs));
        const hasMedia = block.image || block.video || block.videoUrl || block.sourceUrl;
        if (!block.heading.trim() && paragraphs.length === 0 && !hasMedia) return null;

        if (block.videoUrl && !parseMediaUrl(block.videoUrl)) {
          throw new Error("Please enter a valid section video URL.");
        }
        if (block.sourceUrl && !parseMediaUrl(block.sourceUrl)) {
          throw new Error("Please enter a valid source URL.");
        }

        return {
          type: "section",
          heading: block.heading.trim(),
          paragraphs,
          headingFontFamily: block.headingFontFamily,
          headingFontSize: block.headingFontSize,
          headingFontStyle: block.headingFontStyle,
          contentFontFamily: block.contentFontFamily,
          contentFontSize: block.contentFontSize,
          contentFontStyle: block.contentFontStyle,
          image: block.image || "",
          imageAlt: (block.imageAlt || "").trim(),
          video: block.video || "",
          videoUrl: (block.videoUrl || "").trim(),
          videoCaption: (block.videoCaption || "").trim(),
          sourceUrl: (block.sourceUrl || "").trim(),
          sourceLabel: (block.sourceLabel || "").trim(),
        };
      })
      .filter(Boolean);
    } catch (validationError) {
      setError(validationError.message);
      return;
    }

    if (content.length === 0) {
      setError("Add at least one text section, image, video, or URL to the article.");
      return;
    }

    setSaving(true);
    try {
      const saved = await blogService.saveBlog({
        ...form,
        category,
        secondaryKeywords: form.secondaryKeywords,
        tags: form.tags,
        readTime: form.readTime || suggestedReadTime,
        image: form.image || form.bannerImage,
        bannerImage: form.bannerImage || form.image,
        content,
      });
      navigate(`/blogs/${saved.slug}`);
    } catch (saveError) {
      setError(saveError.message || "Could not save this blog.");
    } finally {
      setSaving(false);
    }
  };

  const blockLabel = (block, index) => {
    if (block.type === "image") return "Inline image";
    if (block.type === "video") return "Video";
    if (block.type === "embed") return "YouTube / source URL";
    return `Section ${index + 1}`;
  };

  return (
    <div className={styles.page}>
      <div className={styles.editorHeader}>
        <div>
          <Link to="/admin/blogs" className={styles.ghostBtn}>
            <ArrowLeft size={16} />
            Back to blogs
          </Link>
          <h1>{isEditing ? "Edit blog" : "Write a new blog"}</h1>
          <p className={styles.hint}>
            Add keywords, tags, read time, styled sections, photos, videos, and YouTube or source URLs.
          </p>
        </div>
      </div>

      <form className={styles.form} onSubmit={handleSave}>
        {error ? <div className={styles.error}>{error}</div> : null}
        {uploading ? <div className={styles.success}>{uploading}</div> : null}

        <div className={styles.field}>
          <label htmlFor="title">Title</label>
          <input
            id="title"
            value={form.title}
            onChange={(event) => updateField("title", event.target.value)}
            placeholder="Romantic stay ideas near Tweed Heads South, NSW beaches"
          />
        </div>

        <div className={styles.formRow}>
          <div className={styles.field}>
            <label htmlFor="slug">URL slug</label>
            <input
              id="slug"
              value={form.slug}
              onChange={(event) => updateField("slug", slugify(event.target.value))}
              placeholder="romantic-stay-ideas"
            />
          </div>
          <div className={styles.field}>
            <label htmlFor="category">Category</label>
            <select
              id="category"
              value={form.category}
              onChange={(event) => updateField("category", event.target.value)}
            >
              {categoryOptions.map((category) => (
                <option key={category} value={category}>
                  {category}
                </option>
              ))}
              <option value="custom">Custom category</option>
            </select>
          </div>
        </div>

        {form.category === "custom" ? (
          <div className={styles.field}>
            <label htmlFor="customCategory">Custom category</label>
            <input
              id="customCategory"
              value={form.customCategory}
              onChange={(event) => updateField("customCategory", event.target.value)}
              placeholder="Guest Stories"
            />
          </div>
        ) : null}

        <div className={styles.field}>
          <label htmlFor="primaryKeyword">Primary keyword</label>
          <input
            id="primaryKeyword"
            value={form.primaryKeyword}
            onChange={(event) => updateField("primaryKeyword", event.target.value)}
            placeholder="Tweed Heads South, NSW motel"
          />
        </div>

        <PillInput
          label="Secondary keywords"
          values={form.secondaryKeywords}
          onChange={(value) => updateField("secondaryKeywords", value)}
          placeholder="Type a keyword and press Enter"
          suggestions={SECONDARY_KEYWORD_OPTIONS}
          hint="Add as many as you need. Press Enter or click a suggestion."
        />

        <PillInput
          label="Tags"
          values={form.tags}
          onChange={(value) => updateField("tags", value)}
          placeholder="Type a tag and press Enter"
          suggestions={TAG_OPTIONS}
          hint="Add multiple tags. They will show as pills on the blog page."
        />

        <div className={styles.formRow}>
          <div className={styles.field}>
            <label htmlFor="readTime">Read time (minutes)</label>
            <input
              id="readTime"
              type="number"
              min="1"
              value={form.readTime}
              onChange={(event) => updateField("readTime", event.target.value)}
              placeholder={suggestedReadTime}
            />
            <p className={styles.hint}>
              Leave blank to use the suggested {suggestedReadTime} minute read.
            </p>
          </div>
        </div>

        <div className={styles.field}>
          <label htmlFor="shortDescription">Short description</label>
          <textarea
            id="shortDescription"
            value={form.shortDescription}
            onChange={(event) => updateField("shortDescription", event.target.value)}
            placeholder="A short summary shown on the blogs page and at the top of the article."
          />
        </div>

        <div className={styles.formRow}>
          <div className={`${styles.field} ${styles.imageField}`}>
            <label>Banner image</label>
            {form.bannerImage ? <img src={form.bannerImage} alt="Banner preview" className={styles.preview} /> : null}
            <input
              type="file"
              accept="image/*"
              onChange={(event) =>
                handleImageUpload(event.target.files[0], (src) =>
                  updateField("bannerImage", src)
                )
              }
            />
          </div>

          <div className={`${styles.field} ${styles.imageField}`}>
            <label>Card image</label>
            {form.image ? <img src={form.image} alt="Card preview" className={styles.preview} /> : null}
            <input
              type="file"
              accept="image/*"
              onChange={(event) =>
                handleImageUpload(event.target.files[0], (src) => updateField("image", src))
              }
            />
            <p className={styles.hint}>
              Images and videos upload to Cloudinary. Only the Cloudinary URL is saved with the blog.
              If you skip the card image, the banner is used on the listing card too.
            </p>
          </div>
        </div>

        <div className={styles.checkRow}>
          <label>
            <input
              type="checkbox"
              checked={form.featured}
              onChange={(event) => updateField("featured", event.target.checked)}
            />
            Featured article
          </label>
          <label>
            <input
              type="checkbox"
              checked={form.published}
              onChange={(event) => updateField("published", event.target.checked)}
            />
            Publish on website
          </label>
        </div>

        <div className={styles.blocks}>
          {form.content.map((block, index) => (
            <div className={styles.block} key={`${block.type}-${index}`}>
              <div className={styles.blockTop}>
                <span className={styles.blockTitle}>{blockLabel(block, index)}</span>
                <div className={styles.blockButtons}>
                  <button type="button" className={styles.ghostBtn} onClick={() => moveBlock(index, -1)}>
                    <ChevronUp size={16} />
                  </button>
                  <button type="button" className={styles.ghostBtn} onClick={() => moveBlock(index, 1)}>
                    <ChevronDown size={16} />
                  </button>
                  <button type="button" className={styles.dangerBtn} onClick={() => removeBlock(index)}>
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>

              {block.type === "image" ? (
                <>
                  {block.src ? <img src={block.src} alt={block.alt || "Inline preview"} className={styles.preview} /> : null}
                  <div className={styles.field}>
                    <label>Upload image</label>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(event) =>
                        handleImageUpload(event.target.files[0], (src) =>
                          updateBlock(index, { ...block, src })
                        )
                      }
                    />
                  </div>
                  <div className={styles.formRow}>
                    <div className={styles.field}>
                      <label>Alt text</label>
                      <input
                        value={block.alt}
                        onChange={(event) => updateBlock(index, { ...block, alt: event.target.value })}
                        placeholder="Guest room overlooking the courtyard"
                      />
                    </div>
                    <div className={styles.field}>
                      <label>Caption</label>
                      <input
                        value={block.caption}
                        onChange={(event) =>
                          updateBlock(index, { ...block, caption: event.target.value })
                        }
                        placeholder="Optional caption under the photo"
                      />
                    </div>
                  </div>
                </>
              ) : null}

              {block.type === "video" ? (
                <>
                  {block.src ? (
                    <video className={styles.previewVideo} src={block.src} controls />
                  ) : null}
                  <div className={styles.field}>
                    <label>Upload video</label>
                    <input
                      type="file"
                      accept="video/*"
                      onChange={(event) =>
                        handleVideoUpload(event.target.files[0], (src) =>
                          updateBlock(index, { ...block, src })
                        )
                      }
                    />
                    <p className={styles.hint}>MP4 or WebM works best. Keep the file under 8MB.</p>
                  </div>
                  <div className={styles.field}>
                    <label>Or paste a video URL</label>
                    <input
                      value={block.url}
                      onChange={(event) => updateBlock(index, { ...block, url: event.target.value })}
                      placeholder="https://www.youtube.com/watch?v=..."
                    />
                  </div>
                  <div className={styles.field}>
                    <label>Caption</label>
                    <input
                      value={block.caption}
                      onChange={(event) => updateBlock(index, { ...block, caption: event.target.value })}
                      placeholder="Optional caption under the video"
                    />
                  </div>
                </>
              ) : null}

              {block.type === "embed" ? (
                <>
                  <div className={styles.field}>
                    <label>YouTube or source URL</label>
                    <input
                      value={block.url}
                      onChange={(event) => updateBlock(index, { ...block, url: event.target.value })}
                      placeholder="https://www.youtube.com/watch?v=... or any source link"
                    />
                  </div>
                  <div className={styles.field}>
                    <label>Link title</label>
                    <input
                      value={block.label}
                      onChange={(event) => updateBlock(index, { ...block, label: event.target.value })}
                      placeholder="Watch the Tweed Heads South, NSW stay tour"
                    />
                  </div>
                </>
              ) : null}

              {block.type === "section" ? (
                <>
                  <div className={styles.field}>
                    <label>Heading</label>
                    <input
                      value={block.heading}
                      onChange={(event) =>
                        updateBlock(index, { ...block, heading: event.target.value })
                      }
                      placeholder="Why couples love a Tweed Heads South, NSW stay"
                    />
                  </div>
                  <StyleControls
                    prefix="heading"
                    values={block}
                    onChange={(name, value) => updateBlockField(index, name, value)}
                  />
                  <div className={styles.field}>
                    <label>Content</label>
                    <textarea
                      value={block.body || ""}
                      onChange={(event) =>
                        updateBlock(index, {
                          ...block,
                          body: event.target.value,
                        })
                      }
                      placeholder="Write the section here. Leave a blank line to start a new paragraph."
                    />
                  </div>
                  <StyleControls
                    prefix="content"
                    values={block}
                    onChange={(name, value) => updateBlockField(index, name, value)}
                  />

                  <div className={styles.mediaBox}>
                    <p className={styles.blockTitle}>Section media</p>
                    {block.image ? (
                      <img src={block.image} alt={block.imageAlt || "Section"} className={styles.preview} />
                    ) : null}
                    <div className={styles.formRow}>
                      <div className={styles.field}>
                        <label>Section image</label>
                        <input
                          type="file"
                          accept="image/*"
                          onChange={(event) =>
                            handleImageUpload(event.target.files[0], (src) =>
                              updateBlockField(index, "image", src)
                            )
                          }
                        />
                      </div>
                      <div className={styles.field}>
                        <label>Image alt text</label>
                        <input
                          value={block.imageAlt}
                          onChange={(event) => updateBlockField(index, "imageAlt", event.target.value)}
                          placeholder="Room with coastal light"
                        />
                      </div>
                    </div>

                    {block.video ? <video className={styles.previewVideo} src={block.video} controls /> : null}
                    <div className={styles.formRow}>
                      <div className={styles.field}>
                        <label>Section video upload</label>
                        <input
                          type="file"
                          accept="video/*"
                          onChange={(event) =>
                            handleVideoUpload(event.target.files[0], (src) =>
                              updateBlockField(index, "video", src)
                            )
                          }
                        />
                      </div>
                      <div className={styles.field}>
                        <label>Or video URL</label>
                        <input
                          value={block.videoUrl}
                          onChange={(event) => updateBlockField(index, "videoUrl", event.target.value)}
                          placeholder="https://www.youtube.com/watch?v=..."
                        />
                      </div>
                    </div>
                    <div className={styles.field}>
                      <label>Video caption</label>
                      <input
                        value={block.videoCaption}
                        onChange={(event) => updateBlockField(index, "videoCaption", event.target.value)}
                        placeholder="Optional caption"
                      />
                    </div>
                    <div className={styles.formRow}>
                      <div className={styles.field}>
                        <label>YouTube or source URL</label>
                        <input
                          value={block.sourceUrl}
                          onChange={(event) => updateBlockField(index, "sourceUrl", event.target.value)}
                          placeholder="https://www.youtube.com/watch?v=... or any useful link"
                        />
                      </div>
                      <div className={styles.field}>
                        <label>URL title</label>
                        <input
                          value={block.sourceLabel}
                          onChange={(event) => updateBlockField(index, "sourceLabel", event.target.value)}
                          placeholder="Visit Tweed Heads South, NSW tourism"
                        />
                      </div>
                    </div>
                  </div>
                </>
              ) : null}
            </div>
          ))}
        </div>

        <div className={styles.addRow}>
          <button type="button" className={styles.secondaryBtn} onClick={() => addBlock("section")}>
            <Plus size={16} />
            Add text section
          </button>
          <button type="button" className={styles.secondaryBtn} onClick={() => addBlock("image")}>
            <ImagePlus size={16} />
            Add image
          </button>
          <button type="button" className={styles.secondaryBtn} onClick={() => addBlock("video")}>
            <Video size={16} />
            Add video
          </button>
          <button type="button" className={styles.secondaryBtn} onClick={() => addBlock("embed")}>
            <Link2 size={16} />
            Add YouTube / URL
          </button>
        </div>

        <div className={styles.footerActions}>
          <Link to="/admin/blogs" className={styles.secondaryBtn}>
            Cancel
          </Link>
          <button type="submit" className={styles.primaryBtn} disabled={saving}>
            <Save size={16} />
            {saving ? "Saving..." : "Save blog"}
          </button>
        </div>
      </form>
    </div>
  );
};

export default BlogEditorPage;
